import React, { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  useGetFaces,
  getGetFacesQueryOptions,
  getPatchCardMutationOptions,
  useGetCard,
} from '@vlabs/api/artemy/client';
import type { FaceId, CardId, GetFacesParams } from '@vlabs/api/artemy/model';
import type { QueryClient } from '@vlabs/api/queryClient';
import { MoreHorizontalIcon } from '@vlabs/icons';
import { DeleteFaceDropdownItemPoint3 } from '@vlabs/monokit/features/delete-face/DeleteFaceDropdownItem.point3';
import { SearchFaceButton } from '@vlabs/monokit/features/search-by-face-id';
import { SetFaceAsCardFaceDropdownItemPoint3 } from '@vlabs/monokit/features/set-face-as-card-face';
import { VerifyFaceButton } from '@vlabs/monokit/features/verify-face';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { FaceCardPoint3 } from '@vlabs/monokit/shared/ui/face-card/FaceCard.point3';
import { useHasPermission } from '@vlabs/permissions';
import { Control, DropdownList } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';

import st from './PersonFaceCard.module.sass';

interface PersonFaceCardProps extends WithDataTestid {
  faceId: FaceId;
}

const displayName = 'PersonFaceCard';
const testid = buildTestid(displayName);

export function PersonFaceCard({
  faceId,
  'data-testid': dataTestId = undefined,
}: PersonFaceCardProps): React.JSX.Element {
  const canDeleteFace = useHasPermission('card', 'delete');
  const canUpdateCard = useHasPermission('card', 'update');
  const queryClient = useQueryClient();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const getFacesParams: GetFacesParams = { face_ids: [faceId] };
  const getFace = useGetFaces(getFacesParams);
  const face = getFace.data?.faces?.[0];
  const { data: card } = useGetCard(
    face?.card_id as CardId,
    { targets: ['main_face_id'] },
    { query: { enabled: !!face?.card_id } },
  );

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  const toggleDropdown = useCallback(() => setIsDropdownOpen((prevState) => !prevState), []);

  const isMainFace = card?.main_face_id === face?.face_id;
  const canSetAsMainFace = canUpdateCard && !!face?.card_id && !isMainFace;
  const hasActions = canSetAsMainFace || canDeleteFace;

  const dropdownOptions = [];

  if (canSetAsMainFace) {
    dropdownOptions.push(<SetFaceAsCardFaceDropdownItemPoint3
      cardId={face.card_id}
      faceId={faceId}
      key={face.card_id}
      onSetFaceAsCardFace={closeDropdown}
    />);
  }

  if (canDeleteFace) {
    dropdownOptions.push(<DeleteFaceDropdownItemPoint3
      faceId={faceId}
      onDeleteFace={() => {
        if (getFace.data?.faces?.[0]?.card_id) {
          queryClient.invalidateQueries(getGetFacesQueryOptions({ card_ids: [getFace.data?.faces?.[0]?.card_id] }));
          (queryClient as unknown as QueryClient).mutate(
            getPatchCardMutationOptions(queryClient as unknown as QueryClient),
            { cardId: getFace.data.faces[0].card_id, data: { main_face_id: null } },
          );
        }
        closeDropdown();
      }}
    />);
  }

  return (
    <FaceCardPoint3
      {...testid(dataTestId, face?.face_id)}
      actions={(
        <div className={st.Container}>
          <div className={st.ButtonWrapper}>
            <SearchFaceButton
              className={st.Button}
              disabled
              faceId={faceId}
            />
          </div>
          <div className={st.ButtonWrapper}>
            <VerifyFaceButton
              className={st.Button}
              disabled
              faceId={faceId}
            />
          </div>
          <DropdownList
            isOpen={isDropdownOpen}
            onClose={closeDropdown}
            options={dropdownOptions}
            placement="bottom-right"
          >
            <Control.IconButton
              data-testid="faceCard.actionsButton"
              disabled={!hasActions}
              icon={<MoreHorizontalIcon />}
              kind="secondary"
              onClick={toggleDropdown}
              shape="square"
              size="small"
              variant="outlined"
            />
          </DropdownList>
        </div>
      )}
      className={st.FaceCard}
      faceId={faceId}
      key={faceId}
      shouldShowPin={isMainFace}
      size="large"
    />
  );
}
